// src/services/firebaseservice.js
import { 
    collection, 
    doc, 
    getDoc, 
    getDocs, 
    setDoc, 
    addDoc, 
    updateDoc, 
    deleteDoc, 
    query, 
    where 
  } from "firebase/firestore";
  import { ref, uploadBytesResumable, getDownloadURL, deleteObject, uploadBytes } from "firebase/storage";
  import { db, storage } from "../firebase";
  
  // Admin Services (New)
export const getAllRestaurants = async () => {
  try {
    const restaurantsRef = collection(db, "restaurants");
    const querySnapshot = await getDocs(restaurantsRef);
    const restaurants = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return restaurants;
  } catch (error) {
    console.error("Error fetching all restaurants:", error);
    throw error;
  }
};

export const toggleRestaurantFeature = async (restaurantId, featureName, isActive) => {
  try {
    const restaurantRef = doc(db, "restaurants", restaurantId);
    await updateDoc(restaurantRef, {
      [featureName]: isActive,
      updatedAt: new Date() // Track last modification
    });
    console.log(`Restaurant ${restaurantId} feature '${featureName}' set to ${isActive}`);
    return { restaurantId, featureName, isActive };
  } catch (error) {
    console.error(`Error toggling feature ${featureName} for restaurant ${restaurantId}:`, error);
    throw error;
  }
};

// Example: Deleting all orders for a restaurant. More specific data deletion can be added.
export const deleteRestaurantOrders = async (restaurantId) => {
  try {
    const ordersRef = collection(db, `restaurants/${restaurantId}/orders`);
    const querySnapshot = await getDocs(ordersRef);
    
    const batch = []; // Firestore batch write for deleting multiple documents
    querySnapshot.forEach((document) => {
      batch.push(deleteDoc(doc(ordersRef, document.id)));
    });
    
    await Promise.all(batch); // Execute all delete operations
    console.log(`All orders deleted for restaurant ${restaurantId}`);
    return { message: `All orders deleted for restaurant ${restaurantId}` };
  } catch (error) {
    console.error(`Error deleting orders for restaurant ${restaurantId}:`, error);
    throw error;
  }
};

// Deletes a restaurant and all its associated data including subcollections and storage files.
export const deleteFullRestaurantData = async (restaurantId) => {
  console.log(`Starting full data deletion for restaurant ${restaurantId}`);
  try {
    const restaurantDocRef = doc(db, "restaurants", restaurantId);
    const restaurantSnapshot = await getDoc(restaurantDocRef);
    const restaurantData = restaurantSnapshot.exists() ? restaurantSnapshot.data() : null;

    // 1. Delete menuItems subcollection and their photos
    const menuItemsRef = collection(db, `restaurants/${restaurantId}/menuItems`);
    const menuItemsSnapshot = await getDocs(menuItemsRef);
    if (!menuItemsSnapshot.empty) {
      const deletePromises = menuItemsSnapshot.docs.map(async (menuItemDoc) => {
        const menuItemData = menuItemDoc.data();
        if (menuItemData.photoUrl) {
          try {
            const photoRef = ref(storage, menuItemData.photoUrl);
            await deleteObject(photoRef);
            console.log(`Deleted menu item photo: ${menuItemData.photoUrl}`);
          } catch (error) {
            console.warn(`Failed to delete menu item photo ${menuItemData.photoUrl}:`, error);
          }
        }
        return deleteDoc(menuItemDoc.ref);
      });
      await Promise.all(deletePromises);
      console.log(`Deleted all menu items for restaurant ${restaurantId}`);
    }

    // 2. Delete orders subcollection (using existing function)
    await deleteRestaurantOrders(restaurantId); // This function already handles batching
    console.log(`Deleted all orders for restaurant ${restaurantId}`);

    // 3. Delete analyticsEvents associated with the restaurant
    // Assuming analytics events are in a top-level 'analyticsEvents' collection
    // and have a 'restaurantId' field linking them to the restaurant.
    const analyticsEventsRef = collection(db, "analyticsEvents");
    const analyticsQuery = query(analyticsEventsRef, where("restaurantId", "==", restaurantId));
    const analyticsSnapshot = await getDocs(analyticsQuery);
    if (!analyticsSnapshot.empty) {
      const deleteAnalyticsPromises = analyticsSnapshot.docs.map(docSnapshot => deleteDoc(docSnapshot.ref));
      await Promise.all(deleteAnalyticsPromises);
      console.log(`Deleted analytics events for restaurant ${restaurantId}`);
    }

    // 4. Delete restaurant logo from Storage
    if (restaurantData && restaurantData.logoUrl) {
      try {
        const logoRef = ref(storage, restaurantData.logoUrl);
        await deleteObject(logoRef);
        console.log(`Deleted restaurant logo: ${restaurantData.logoUrl}`);
      } catch (error) {
        console.warn(`Failed to delete restaurant logo ${restaurantData.logoUrl}:`, error);
      }
    }

    // 5. Delete the main restaurant document
    await deleteDoc(restaurantDocRef);
    console.log(`Restaurant ${restaurantId} and all its data have been deleted.`);

    return { message: `Restaurant ${restaurantId} and all its data have been successfully deleted.` };

  } catch (error) {
    console.error(`Error deleting full restaurant data for ${restaurantId}:`, error);
    throw error; // Re-throw the error to be caught by the calling UI
  }
};


// Restaurant Services
  export const createRestaurant = async (restaurantData) => {
    try {
      // Check if slug is already taken
      const slugCheckQuery = query(
        collection(db, "restaurants"), 
        where("slug", "==", restaurantData.slug)
      );
      
      const slugCheck = await getDocs(slugCheckQuery);
      if (!slugCheck.empty) {
        throw new Error("Restaurant slug already exists. Please choose another one.");
      }
      
      // Create new restaurant document
      const newRestaurantRef = doc(collection(db, "restaurants"));
      await setDoc(newRestaurantRef, {
        ...restaurantData,
        createdAt: new Date(),
      });
      
      return { id: newRestaurantRef.id, ...restaurantData };
    } catch (error) {
      console.error("Error creating restaurant:", error);
      throw error;
    }
  };
  
  export const getRestaurantBySlug = async (slug) => {
    try {
      console.log("Searching for restaurant with slug:", slug);
      
      // Validate slug input
      if (!slug) {
        console.error("Invalid slug provided:", slug);
        return null;
      }
      
      const restaurantsRef = collection(db, "restaurants");
      const q = query(restaurantsRef, where("slug", "==", slug));
      
      console.log("Executing Firestore query...");
      const querySnapshot = await getDocs(q);
      console.log("Query returned", querySnapshot.size, "results");
      
      if (querySnapshot.empty) {
        console.log("No restaurant found with slug:", slug);
        return null;
      }
      
      const restaurantDoc = querySnapshot.docs[0];
      const restaurantData = {
        id: restaurantDoc.id,
        ...restaurantDoc.data()
      };
      
      console.log("Found restaurant:", restaurantData.name);
      return restaurantData;
    } catch (error) {
      console.error("Error getting restaurant by slug:", error);
      // Log additional information about the error
      console.error("Error details:", {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      throw error;
    }
  };
  
  // New function to get restaurant details by ID
  export const getRestaurantDetails = async (restaurantId) => {
    try {
      const restaurantDocRef = doc(db, 'restaurants', restaurantId);
      const restaurantSnap = await getDoc(restaurantDocRef);
  
      if (!restaurantSnap.exists()) {
        console.log("No restaurant found with ID:", restaurantId);
        return null;
      }
  
      const restaurantData = { id: restaurantSnap.id, ...restaurantSnap.data() };
      return restaurantData;
    } catch (error) {
      console.error("Error getting restaurant details:", error);
      throw error;
    }
  };

  export const updateRestaurantLogo = async (restaurantId, logoFile) => {
    try {
      console.log("Starting logo upload for restaurant:", restaurantId);
      console.log("Logo file:", logoFile ? logoFile.name : "No file provided");
      
      if (!logoFile) {
        console.warn("No logo file provided for upload");
        return null;
      }
      
      // Create a unique filename to prevent overwrites
      const fileName = `${Date.now()}_${logoFile.name}`;
      console.log("Generated unique filename:", fileName);
      
      // Upload logo to Firebase Storage
      const logoRef = ref(storage, `restaurants/${restaurantId}/logo/${fileName}`);
      
      console.log("Starting upload to Storage...");
      
      // Convert File object to Blob if needed
      const fileBlob = logoFile instanceof Blob ? logoFile : new Blob([logoFile], { type: logoFile.type });
      
      // Upload the file
      const uploadTask = uploadBytesResumable(logoRef, fileBlob);
      
      // Wait for the upload to complete
      await uploadTask;
      console.log("Upload complete:", uploadTask.snapshot);
      
      // Get the download URL
      console.log("Getting download URL...");
      const logoUrl = await getDownloadURL(logoRef);
      console.log("Download URL obtained:", logoUrl);
      
      // Update restaurant document with logo URL
      const restaurantRef = doc(db, "restaurants", restaurantId);
      await updateDoc(restaurantRef, {
        logoUrl: logoUrl
      });
      console.log("Restaurant document updated with logo URL");
      
      return logoUrl;
    } catch (error) {
      console.error("Error updating restaurant logo:", error);
      console.error("Error details:", {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      
      // Check for specific Firebase Storage errors
      if (error.code?.includes('storage/')) {
        console.error("Firebase Storage error detected");
      }
      
      throw error;
    }
  };



// Analytics Services (New)
export const logDetailedEvent = async (restaurantId, eventName, eventData) => {
  try {
    const analyticsRef = collection(db, "analyticsEvents");
    
    // Ensure eventData.page_path is not undefined
    const sanitizedEventData = { ...eventData };
    if (sanitizedEventData && typeof sanitizedEventData.page_path === 'undefined') {
      sanitizedEventData.page_path = '/'; // Default to '/' if undefined
    }

    await addDoc(analyticsRef, {
      restaurantId: restaurantId, // Store the restaurantId explicitly
      eventName: eventName,
      eventData: sanitizedEventData, // Store the sanitized eventData object
      timestamp: new Date(),
    });
    // console.log(`Detailed event logged: ${eventName} for restaurant ${restaurantId || 'N/A'}`, sanitizedEventData);
    return { success: true, eventName, eventData: sanitizedEventData };
  } catch (error) {
    console.error("Error logging detailed event:", error);
    throw error;
  }
};

export const getAnalyticsEvents = async (restaurantId) => {
  try {
    if (!restaurantId) {
        // console.log("getAnalyticsEvents: restaurantId not provided, returning empty array.");
        return [];
    }
    const analyticsRef = collection(db, "analyticsEvents");
    const q = query(analyticsRef, where("restaurantId", "==", restaurantId));
    const querySnapshot = await getDocs(q);
    const events = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    // console.log(`Fetched ${events.length} analytics events for restaurant: ${restaurantId}`);
    return events;
  } catch (error) {
    console.error(`Error fetching analytics events for restaurant ${restaurantId}:`, error);
    throw error;
  }
};

// // Order Services
// Helper function to generate the next order number
const getNextOrderNumber = async (restaurantId) => {
  const ordersRef = collection(db, `restaurants/${restaurantId}/orders`);
  // Query for orders sorted by orderNumber in descending order and limit to 1 to get the highest efficiently.
  // However, Firestore requires a composite index for this kind of query if orderNumber is not the first orderBy field.
  // For simplicity now, we fetch all and find max. If performance issues arise, create the index.
  const q = query(ordersRef);
  const querySnapshot = await getDocs(q);
  let maxOrderNumber = 0;
  querySnapshot.forEach(doc => {
    const order = doc.data();
    if (order.orderNumber) {
      const num = parseInt(order.orderNumber, 10);
      if (!isNaN(num) && num > maxOrderNumber) {
        maxOrderNumber = num;
      }
    }
  });
  // Ensure the number is at least 1 and pads to 3 digits.
  return (maxOrderNumber + 1).toString().padStart(3, '0');
};

// New function to update order status
export const updateOrderStatus = async (restaurantId, orderId, status, cancellationReason = null) => {
  try {
    const orderRef = doc(db, `restaurants/${restaurantId}/orders`, orderId);
    const updateData = {
      status: status,
      updatedAt: new Date() // Optional: track when the status was updated
    };
    
    // Add cancellation reason if provided and status is cancelled
    if (status === 'cancelled' && cancellationReason) {
      updateData.cancellationReason = cancellationReason;
    }
    
    await updateDoc(orderRef, updateData);
    console.log(`Order ${orderId} status updated to ${status} for restaurant ${restaurantId}`);
  } catch (error) {
    console.error("Error updating order status:", error);
    throw error;
  }
};

// Function to update order items
export const updateOrderItems = async (restaurantId, orderId, items, totalAmount) => {
  try {
    const orderRef = doc(db, `restaurants/${restaurantId}/orders`, orderId);
    await updateDoc(orderRef, {
      items: items,
      totalAmount: totalAmount,
      updatedAt: new Date() // Track when the items were updated
    });
    console.log(`Order ${orderId} items updated for restaurant ${restaurantId}`);
    return { success: true };
  } catch (error) {
    console.error("Error updating order items:", error);
    throw error;
  }
};

export const updateOrderDetails = async (restaurantId, orderId, updates) => {
  try {
    const orderRef = doc(db, `restaurants/${restaurantId}/orders`, orderId);
    await updateDoc(orderRef, {
      ...updates,
      updatedAt: new Date()
    });
    console.log(`Order ${orderId} details updated for restaurant ${restaurantId}`);
    return { success: true };
  } catch (error) {
    console.error("Error updating order details:", error);
    throw error;
  }
};

export const getOrdersByRestaurantId = async (restaurantId) => {
  try {
    const ordersRef = collection(db, `restaurants/${restaurantId}/orders`);
    const q = query(ordersRef);
    const querySnapshot = await getDocs(q);
    const orders = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return orders;
  } catch (error) {
    console.error("Error fetching orders:", error);
    throw error;
  }
};

// Menu Item Services
  export const addMenuItem = async (restaurantId, menuItemData, photoFile) => {
    try {
      console.log("Adding menu item for restaurant:", restaurantId);
      console.log("Menu item data:", menuItemData);
      console.log("Photo file:", photoFile ? photoFile.name : "No file provided");
      
      let photoUrl = null;
      
      // Upload photo if provided
      if (photoFile) {
        try {
          console.log("Starting photo upload process...");
          
          // Create a unique filename with timestamp
          const fileName = `${Date.now()}_${photoFile.name}`;
          console.log("Generated unique filename:", fileName);
          
          const photoRef = ref(storage, `restaurants/${restaurantId}/menu/${fileName}`);
          
          // Convert File object to Blob if needed
          const fileBlob = photoFile instanceof Blob ? photoFile : new Blob([photoFile], { type: photoFile.type });
          
          // Log file details for debugging
          console.log("File details:", {
            size: fileBlob.size,
            type: fileBlob.type,
          });
          
          // Upload the file with explicit content type
          const metadata = {
            contentType: photoFile.type || 'image/jpeg' // Fallback to jpeg if type is missing
          };
          
          console.log("Starting upload to Firebase Storage...");
          const uploadTask = await uploadBytes(photoRef, fileBlob, metadata);
          console.log("Upload complete:", uploadTask);
          
          console.log("Getting download URL...");
          photoUrl = await getDownloadURL(photoRef);
          console.log("Download URL obtained:", photoUrl);
        } catch (uploadError) {
          console.error("Error uploading photo:", uploadError);
          throw new Error(`Photo upload failed: ${uploadError.message}`);
        }
      }
      
      console.log("Creating menu item in Firestore...");
      // Add menu item to subcollection
      const menuItemsRef = collection(db, `restaurants/${restaurantId}/menuItems`);
      const newItemRef = await addDoc(menuItemsRef, {
        ...menuItemData,
        photoUrl,
        createdAt: new Date()
      });
      console.log("Menu item created with ID:", newItemRef.id);
      
      return {
        id: newItemRef.id,
        ...menuItemData,
        photoUrl
      };
    } catch (error) {
      console.error("Error adding menu item:", error);
      console.error("Error details:", {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      throw error;
    }
  };
  
  export const getMenuItems = async (restaurantId) => {
    try {
      const menuItemsRef = collection(db, `restaurants/${restaurantId}/menuItems`);
      const querySnapshot = await getDocs(menuItemsRef);
      
      const menuItems = [];
      querySnapshot.forEach((doc) => {
        menuItems.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return menuItems;
    } catch (error) {
      console.error("Error getting menu items:", error);
      throw error;
    }
  };
  
  export const updateMenuItem = async (restaurantId, itemId, menuItemData, photoFile) => {
    try {
      const menuItemRef = doc(db, `restaurants/${restaurantId}/menuItems`, itemId);
      const itemSnapshot = await getDoc(menuItemRef);
      
      if (!itemSnapshot.exists()) {
        throw new Error("Menu item not found");
      }
      
      let photoUrl = menuItemData.photoUrl;
      
      // Upload new photo if provided
      if (photoFile) {
        // Delete old photo if exists
        if (photoUrl) {
          try {
            const oldPhotoRef = ref(storage, photoUrl);
            await deleteObject(oldPhotoRef);
          } catch (deleteError) {
            console.warn("Could not delete old photo:", deleteError);
          }
        }
        
        // Upload new photo
        const photoRef = ref(storage, `restaurants/${restaurantId}/menu/${Date.now()}_${photoFile.name}`);
        await uploadBytes(photoRef, photoFile);
        photoUrl = await getDownloadURL(photoRef);
      }
      
      // Update menu item
      await updateDoc(menuItemRef, {
        ...menuItemData,
        photoUrl,
        updatedAt: new Date()
      });
      
      return {
        id: itemId,
        ...menuItemData,
        photoUrl
      };
    } catch (error) {
      console.error("Error updating menu item:", error);
      throw error;
    }
  };
  
  export const deleteMenuItem = async (restaurantId, itemId) => {
    try {
      // Get the item first to check for photo URL
      const menuItemRef = doc(db, `restaurants/${restaurantId}/menuItems`, itemId);
      const itemSnapshot = await getDoc(menuItemRef);
      
      if (!itemSnapshot.exists()) {
        throw new Error("Menu item not found");
      }
      
      const itemData = itemSnapshot.data();
      
      // Delete photo from storage if exists
      if (itemData.photoUrl) {
        try {
          const photoRef = ref(storage, itemData.photoUrl);
          await deleteObject(photoRef);
        } catch (deleteError) {
          console.warn("Could not delete photo:", deleteError);
        }
      }
      
      // Delete menu item document
      await deleteDoc(menuItemRef);
      
      return itemId;
    } catch (error) {
      console.error("Error deleting menu item:", error);
      throw error;
    }
  };
// Order Services
  export const addOrder = async (restaurantId, orderData) => {
    try {
      const nextOrderNum = await getNextOrderNumber(restaurantId);
      const fullOrderData = {
        ...orderData,
        orderNumber: nextOrderNum,
        createdAt: new Date(), // Timestamp for when the order was created
      };
      // Add a new order to the restaurant's orders subcollection
      const ordersRef = collection(db, `restaurants/${restaurantId}/orders`);
      const newOrderRef = await addDoc(ordersRef, fullOrderData);
      // Ensure the response object explicitly uses nextOrderNum for orderNumber
      const responseData = {
        id: newOrderRef.id,
        ...fullOrderData
      };
      responseData.orderNumber = nextOrderNum; // Explicitly set to ensure it's the 3-digit number
      return responseData;
    } catch (error) {
      console.error("Error adding order:", error);
      throw error;
    }
  };

// Workflow-related functions
export const getRestaurantWorkflow = async (restaurantId) => {
  try {
    const restaurantRef = doc(db, "restaurants", restaurantId);
    const restaurantSnap = await getDoc(restaurantRef);
    
    if (restaurantSnap.exists()) {
      const data = restaurantSnap.data();
      // Return custom workflow or default workflow
      return data.orderWorkflow || [
        { id: 'new', name: 'New Orders', isDefault: true, canDelete: false },
        { id: 'completed', name: 'Order History', isDefault: true, canDelete: false },
        { id: 'cancelled', name: 'Cancelled Orders', isDefault: true, canDelete: false }
      ];
    }
    throw new Error('Restaurant not found');
  } catch (error) {
    console.error("Error fetching restaurant workflow:", error);
    throw error;
  }
};

export const updateRestaurantWorkflow = async (restaurantId, workflow) => {
  try {
    const restaurantRef = doc(db, "restaurants", restaurantId);
    await updateDoc(restaurantRef, {
      orderWorkflow: workflow,
      updatedAt: new Date()
    });
    console.log(`Workflow updated for restaurant ${restaurantId}`);
  } catch (error) {
    console.error("Error updating restaurant workflow:", error);
    throw error;
  }
};

export const moveOrderToNextStep = async (restaurantId, orderId) => {
  try {
    // Get the current order to determine its status
    const orderRef = doc(db, `restaurants/${restaurantId}/orders`, orderId);
    const orderSnap = await getDoc(orderRef);
    
    if (!orderSnap.exists()) {
      throw new Error('Order not found');
    }
    
    const currentOrder = orderSnap.data();
    const currentStatus = currentOrder.status || 'new';
    
    // Get the restaurant workflow to determine next step
    const workflow = await getRestaurantWorkflow(restaurantId);
    const currentStepIndex = workflow.findIndex(step => step.id === currentStatus);
    
    if (currentStepIndex === -1) {
      throw new Error('Current status not found in workflow');
    }
    
    // Find the next step (skip to completed if at the end)
    let nextStatus;
    if (currentStepIndex < workflow.length - 1) {
      // Find next non-cancelled step
      const nextSteps = workflow.slice(currentStepIndex + 1);
      const nextStep = nextSteps.find(step => step.id !== 'cancelled');
      nextStatus = nextStep ? nextStep.id : 'completed';
    } else {
      nextStatus = 'completed';
    }
    
    await updateDoc(orderRef, {
      status: nextStatus,
      previousStatus: currentStatus,
      updatedAt: new Date()
    });
    
    console.log(`Order ${orderId} moved from ${currentStatus} to ${nextStatus}`);
  } catch (error) {
    console.error("Error moving order to next step:", error);
    throw error;
  }
};